import { useEffect, useState, useRef } from "react";
import _ from "lodash";
import ZPLLogoCommandMap from "../../img/logo_in_zpl_commands";
import { naturalSort } from "../../common/Utilities";

const EXTRA_SMALL_FONT = "14,10";
const SMALL_FONT = "24,14";
const MEDIUM_FONT = "32,20";
const LARGE_FONT = "44,24";
const EXTRA_LARGE_FONT = "68,40";

const PRINTER_STATUS = {
  ONLINE: {
    label: "Online",
    color: "green"
  },
  OFFLINE: {
    label: "Offline",
    color: "red"
  }
};

const ONE_MINUTE = 60000;

const calculateLabelContainerSize = (tagSizeInDots, numberOfColumns, numberOfRows, workOrderStatus) => {
  const { x, y } = tagSizeInDots;
  let headerHeight = 50;
  let lineBreakGap = 50;
  let workOrderStatusContainer = 100;

  // 300dpi, 4x6 tag
  if (x === 1800 && y === 1200) {
    headerHeight = 100;
  }

  // 203 dpi, 2x4 tag
  if (x === 812 && y === 406) {
    headerHeight = 25;
    lineBreakGap = 25;
    workOrderStatusContainer = 65;
  }

  const containerWidth = Math.floor((x - 100 - 50 * (numberOfColumns + 1)) / numberOfColumns);
  const containerHeight = Math.floor(
    (y -
      (100 +
        (lineBreakGap * (numberOfRows + 1) +
          headerHeight * numberOfRows +
          (workOrderStatus ? workOrderStatusContainer : 0)))) /
      numberOfRows
  );
  return { containerWidth, containerHeight };
};

export const generateTagBorderCommand = (tagSizeInDots) => {
  const { x, y } = tagSizeInDots;

  const borderXLength = x - 100;
  const borderYLength = y - 100;

  return `^FO50,50^GB${borderXLength},5,5^FS
    ^FO50,${borderYLength + 50}^GB${borderXLength + 5},5,5^FS
    ^FO50,50^GB5,${borderYLength},5^FS
    ^FO${borderXLength + 50},50^GB5,${borderYLength + 5},5^FS`;
};

export const generateWOStatusCommand = (WOStatus, tagSizeInDots) => {
  const { x, y } = tagSizeInDots;
  const xCoordinate = x - 100;
  let yCoordinate = y - 150;
  let fontSize = 75;

  // 2x4 tag, 203 dpi
  if (x === 812 && y === 406) {
    fontSize = 50;
    yCoordinate = y - 100;
  }
  // vertical tag
  if (x < y) {
    return `^FO 150,150,1^A0N,100^TBR,1000,80^FD${WOStatus || ""}^FS`;
  }
  return `^FO${xCoordinate},${yCoordinate},1^A0N,${fontSize}^FD${WOStatus}^FS`;
};

export const generateBarcodeTagCommand = (tagToPrint, logo, tagSize) => {
  const { x, y } = tagSize;

  const rfidLogo =
    "^FO950,100^GFA,2850,2850,19,Y0FFE,W07KFC,V0NFC,U03OFC,U03PF8,U07PFE,U07QFC,U03IF803LF,U03F8K07JFC,V08M03JF,gK07IF8,gL0IFE,gL03IF,gM0IFC,gM03FFE,gN0IF,gN03FFC,X01IFL01FFE,W03KF8K07FF,7QF8I01MF8J03FF8,RFCI03NFJ01FFC,RFCI03NFCJ07FE,RFCI07OFJ03FF,RFCI03OFCI01FF8,RF8I03FEI0KFJ0FFC,RFK0CK07IFCI07FC,FEgH0IFEI03FE,FEgH01IF8001FF,FEgI07FFCI0FF8,FEgI01FFEI07F8,FEgJ07FFI07FC,FEgJ03FF8003FE,FEgK0FFC001FE,FEgK07FEI0FF,FEV03IF8J03FFI0FF8,FEU01KF8I01FF8007F8,FEU03KFEJ0FFC003FC,FEU03LF8I07FE003FC,FEU03LFEI03FE001FE,FEU03MF8001FF001FE,FEU03MFCI0FF800FF,FEV0EI0JFI07F800FF,FEg01IF8003FC007F,FEgG03FFC003FC007F8,FEgH0FFE001FE003F8,FEgH07FFI0FE003FC,FEgH01FF800FF001FC,FEgI0FF8007F001FC,FEgI07FC007F801FE,FEgI03FE003F800FE,FEgI01FE003FC00FE,FEgJ0FF001FC00FE,FEgJ0FF001FC007F,FEgJ07F801FE007F,FEgJ03F800FE007F,FEX07F8I03FC00FE007F,FEW01FFEI01FC00FE007F8FEW03IFI01FE007F003F8FEW07IF8I0FE007F003F8FEW0JFCI0FE007F003F8:FEV01JFEI0FF007F003F8FEV01JFEI07F003F803F8FEV01JFEI07F003F803FCFEV01JFEI07F003F801FC::::FEW0JFCI07F003F801FCFEW07IF8I07F003F801FC:FEW03IFJ07F003F801FCFEX0FFCJ07F003F801FCFEX03FK07F003F803F8FEgK07F007F003F8FEgK0FE007F003F8:FEgK07E007F003F8FEgK03C007F003F8FEgO0FF003F8FEgO0FE007F,FEgO07E007F,FEgO07C007F,FEgO01I07F,FEgS07F,FEgS07E,:FEgS018,FE,::::::::::FEgO07C,FEgO07E,FEgO0FE,FEI0IFJ07IF801R0FE,FE003JF001JFE07C07JFK0FE,FE007JFC01KF0FE07JFCJ0FE,FE007JFE03KF0FE0KFEJ0FE,FE007KF03KF0FE0LF8I0FE,FE007KF03JFE0FE0LFCI0FE,FE007KF83JFE0FE0LFCI0FE,FE007F00FF83FCJ0FE0FF03FFEI0FE,FE007F003F83F8J0FE0FE003FFI0FE,FE007F003FC3F8J0FE0FEI0FFI0FE,FE007F001FC3F8J0FE0FEI07F800FE,FE007F003FC3F8J0FE0FEI07F800FE,FE007F003F83FCJ0FE0FEI03F800FE,FE007F007F83JF80FE0FEI03FC00FE,FE007KF83JFC0FE0FEI01FC00FE,FE007KF03JFE0FE0FEI01FC00FE,:FE007JFE03JFE0FE0FEI01FC00FE,FE007JFC03JFC0FE0FEI01FC00FE,FE007JF003JF80FE0FEI03FC00FE,FE007JF003F8J0FE0FEI03F800FE,FE007F07F803F8J0FE0FEI07F800FE,FE007F07F803F8J0FE0FEI0FF800FE,FE007F03FC03F8J0FE0FE001FFI0FE,FE007F01FE03F8J0FE0FE007FFI0FE,FE007F01FE03F8J0FE0LFEI0FE,FE007F00FF03F8J0FE0LFCI0FE,FE007F00FF03F8J0FE0LF8I0FE,FE007F007F83F8J0FE0LFJ0FE,FE007F003F03F8J0FE0KFEJ0FE,FE003E003F01F8J07C07JF8J0FE,FE001E001E00FK07803IFEK0FE,FEgO0FE,:::::::gRFE,::::7gQFC,3gQF8,^FS";

  if (x === 4 && y === 2) {
    return `^XA
    ${rfidLogo}
    ^FO100,100^BQN,2,6^FDMM,A${tagToPrint}^FS
    ${logo}
    ^FO0,300^A0N,50,^FB1200,3,0,C,0,^FDXemelgo RFID Tag\&^FS
    ^FO0,400^A0N,50,^FB1200,3,0,C,0,^FD${tagToPrint}\&^FS
    ^FO${545 - (tagToPrint.length - 1) * 17},450^BY3^BCN,100,N,N,N^FD${tagToPrint}^FS
    ^XZ`;
  }

  if (x === 4 && y === 1) {
    return `^XA
    ${rfidLogo}
    ^FO100,110^BQN,2,6^FDMM,A${tagToPrint}^FS
    ^FO0,200^ASN,50,50,^FB1200,1,0,C,0,^FDXemelgo RFID Tag\&^FS
    ^FO0,75^ASN,50,50,^FB1200,1,0,C,0,^FD${tagToPrint}\&^FS
    ^FO${560 - (tagToPrint.length - 1) * 10},125^BY2^BCN,50,N,N,N^FD${tagToPrint}^FS
    ^XZ`;
  }

  return "";
};

export const generateTagLabelsCommand = (labelList, tagSizeInDots, workOrderStatus = undefined) => {
  const { x, y } = tagSizeInDots;
  let headersPerRow = 3;
  let numberOfColumns = 3;
  let numberOfRows = 2;

  // label's attributes size
  let newLineGap = 50;
  let labelGap = 50;
  let headerHeight = 50;

  // font size
  let headerFontSize = MEDIUM_FONT;
  let dataFontSize = LARGE_FONT;

  // 300dpi 4x6 tag properties
  if (x === 1800 && y === 1200) {
    newLineGap = 75;
    headerFontSize = LARGE_FONT;
    dataFontSize = EXTRA_LARGE_FONT;
  }

  // 300dpi 2x4 tag
  if (x === 1200 && y === 600) {
    headerFontSize = SMALL_FONT;
    dataFontSize = MEDIUM_FONT;
  }

  // 203dpi 2x4 tag
  if (x === 812 && y === 406) {
    newLineGap = 25;
    labelGap = 25;
    headerHeight = 25;
    headerFontSize = EXTRA_SMALL_FONT;
    dataFontSize = SMALL_FONT;
  }

  // overSizeTag;
  if (labelList.length > 6) {
    headersPerRow = 4;
    numberOfColumns = 4;
    numberOfRows = 3;
    headerFontSize = MEDIUM_FONT;
    dataFontSize = LARGE_FONT;
  }
  const { containerWidth, containerHeight } = calculateLabelContainerSize(
    tagSizeInDots,
    numberOfColumns,
    numberOfRows,
    workOrderStatus
  );

  const headerContainerSize = `${containerWidth},100`;
  const columnWidth = containerWidth + labelGap;
  const rowHeight = containerHeight + labelGap + headerHeight;

  const sortedLabelList = labelList.sort((a, b) => {
    return a.index - b.index;
  });

  return sortedLabelList
    .map((eachLabel) => {
      let isHeaderOversize = false;
      const { label, index, value } = eachLabel;
      const columnIndex = index % headersPerRow;
      const rowIndex = Math.floor(index / headersPerRow);

      if (label.length > 12) {
        isHeaderOversize = true;
      }
      // vertical tag
      if (x < y) {
        return `
        ^FO${625 - rowIndex * (225 + newLineGap)},${
          100 + columnIndex * (306 + labelGap)
        }^AAN,32,20^TBR,306,100^FD${label}:^FS
        ^FO${500 - rowIndex * (225 + newLineGap)},${100 + columnIndex * (306 + labelGap)}^AAN,44,24^TBR,306,${
          isHeaderOversize ? 135 : 160
        }^FD${value}^FS`;
      }

      // normal orientation tag
      return `
      ^FO${100 + columnIndex * columnWidth},${
        100 + rowIndex * rowHeight
      }^AAN,${headerFontSize}^TBN,${headerContainerSize}^FD${label}:^FS
      ^FO${100 + columnIndex * columnWidth},${
        100 + newLineGap + rowIndex * rowHeight + (isHeaderOversize ? 25 : 0)
      }^AAN,${dataFontSize}^TBN,${containerWidth},${containerHeight - (isHeaderOversize ? 25 : 0)}^FD${value}^FS`;
    })
    .join("");
};

export const useImportTagPrinterScripts = () => {
  const [isPrinterScriptsReady, setIsPrinterScriptsReady] = useState(false);
  const [selectedPrinterInfo, setSelectedPrinterInfo] = useState({});
  const [zebraPrinter, setZebraPrinter] = useState({});
  const intervalId = useRef(null);

  // register zebraPrinter object for other function to make request to the printer
  const registerPrinter = async (newPrinter) => {
    if (!Object.keys(newPrinter).length) {
      setSelectedPrinterInfo({});
      setZebraPrinter({});
      return;
    }

    const { id, name, label, printerObject } = newPrinter;
    const newZebraPrinter = new window.Zebra.Printer(printerObject);

    // default printer info
    const newSelectedPrinterInfo = {
      name: { hiddenOnInfoCard: true, value: name, label },
      message: {
        label: "Status",
        value: "--"
      },
      id: { label: "IP Address", value: id }
    };
    setSelectedPrinterInfo(newSelectedPrinterInfo);
    setZebraPrinter(newZebraPrinter);

    // restart get printer status when new printer selected ( or after a print job)
    stopGetPrinterStatus();
    await startGetPrinterStatus(newZebraPrinter, newSelectedPrinterInfo);
  };

  // update printer info
  const setPrinterStatus = (newPrinterStatus, newSelectedPrinterInfo) => {
    const { message, readyToPrint } = newPrinterStatus;
    const clonedNewSelectedPrnterInfo = { ...newSelectedPrinterInfo };
    clonedNewSelectedPrnterInfo.message.value = readyToPrint ? PRINTER_STATUS.ONLINE.label : message;
    clonedNewSelectedPrnterInfo.message.statusColor = readyToPrint
      ? PRINTER_STATUS.ONLINE.color
      : PRINTER_STATUS.OFFLINE.color;
    setSelectedPrinterInfo(clonedNewSelectedPrnterInfo);
  };

  // get printer status for new printer and set up interval to get printer status
  const startGetPrinterStatus = async (newZebraPrinter, newSelectedPrinterInfo) => {
    if (!Object.keys(newZebraPrinter).length) {
      return;
    }
    await newZebraPrinter.getStatus((status) => {
      const message = status.getMessage();
      const readyToPrint = status.isPrinterReady();
      setPrinterStatus({ message, readyToPrint }, newSelectedPrinterInfo);
    });

    const interval = setInterval(async () => {
      await newZebraPrinter.getStatus((status) => {
        const message = status.getMessage();
        const readyToPrint = status.isPrinterReady();
        setPrinterStatus({ message, readyToPrint }, newSelectedPrinterInfo);
      });
    }, ONE_MINUTE);

    intervalId.current = interval;
  };

  const stopGetPrinterStatus = () => {
    clearInterval(intervalId.current);
  };

  // stop get printer status interval when dismount
  useEffect(() => {
    return () => {
      stopGetPrinterStatus();
    };
  }, []);

  useEffect(() => {
    let browserScript;
    let zebraBrowserScript;

    let browserScriptLoaded = false;
    let zebraBrowserScriptLoaded = false;

    const existingBrowserScript = document.getElementById("browserPrint");
    if (!existingBrowserScript) {
      browserScript = document.createElement("script");
      browserScript.type = "text/javascript";
      browserScript.src = "zebra-printer/BrowserPrint.js";
      browserScript.id = "browserPrint";
      document.body.appendChild(browserScript);

      // if both the required script are loaded, run the callback
      browserScript.onload = () => {
        browserScriptLoaded = true;
        if (zebraBrowserScriptLoaded) {
          setIsPrinterScriptsReady(true);
        }
      };
    }

    const existingZebraBrowserScript = document.getElementById("browserPrintZebra");
    if (!existingZebraBrowserScript) {
      zebraBrowserScript = document.createElement("script");
      zebraBrowserScript.type = "text/javascript";
      zebraBrowserScript.src = "zebra-printer/BrowserPrint-Zebra.js";
      zebraBrowserScript.id = "browserPrintZebra";
      document.body.appendChild(zebraBrowserScript);

      // if both the required script are loaded, run the callback
      zebraBrowserScript.onload = () => {
        zebraBrowserScriptLoaded = true;
        if (browserScriptLoaded) {
          setIsPrinterScriptsReady(true);
        }
      };
    }

    if (existingBrowserScript && existingZebraBrowserScript) {
      setIsPrinterScriptsReady(true);
    }

    return () => {
      if (existingBrowserScript && existingZebraBrowserScript) {
        existingBrowserScript.remove();
        existingZebraBrowserScript.remove();
      }
      if (browserScript) {
        document.body.removeChild(browserScript);
        document.body.removeChild(zebraBrowserScript);
      }
    };
  }, []);

  const getDefaultPrinter = () => {
    return new Promise((resolve, reject) => {
      window.BrowserPrint.getDefaultDevice(
        "printer",
        (device) => {
          resolve(device);
        },
        (error) => {
          reject(
            `Cannot query for printers.
          Ensure that Zebra Browser Print is running.`
          );
        }
      );
    });
  };

  const getAvailablePrinters = () => {
    return new Promise((resolve, reject) => {
      window.BrowserPrint.getLocalDevices(
        (deviceList) => {
          resolve(deviceList.printer);
        },
        (error) => {
          reject(`Cannot query for printers`);
        }
      );
    });
  };

  const queryPrinters = async (setLoading, setPrinters, onSelectPrinter, setError, setErrorMessage) => {
    setLoading(true);
    const defaultPrinterPromise = getDefaultPrinter();
    const availablePrintersPromise = getAvailablePrinters();

    try {
      const [defaultPrinter = {}, availablePrinters = []] = await Promise.all([
        defaultPrinterPromise,
        availablePrintersPromise
      ]);

      let printersToSet = availablePrinters.map((printer) => {
        const { uid: id, name } = printer;
        return { id, name, label: name, printerObject: printer };
      });
      printersToSet = naturalSort(printersToSet, "label");

      const defaultPrinterToSet = printersToSet.find((printer) => {
        return printer.id === defaultPrinter.uid;
      });

      setPrinters(printersToSet);
      onSelectPrinter(defaultPrinterToSet);
    } catch (e) {
      setLoading(false);
      setError(true);
      setErrorMessage(e);
    }
  };

  const printTag = async (tagToPrint, item) => {
    const { name, color_ts, sku_ts } = item;

    const printZPLCommand = `^XA^RS8^FO200,70^A0N,50^FD${name.value || ""}^FS^FO200,140^A0N,50^FD${
      color_ts.value || ""
    }^FS^FO200,210^A0N,50^FD${sku_ts.value || ""}^FS^FO200,400^BY4^BCI,100,Y,N,N^FD${
      tagToPrint || ""
    }^FS^FO1000,70^BQN,2,6^FDMM,A${tagToPrint || ""}^FS^RFW,H^FD${tagToPrint || ""}^FS^XZ`;

    return new Promise((resolve, reject) => {
      zebraPrinter.send(
        printZPLCommand,
        (success) => {
          resolve(success);
        },
        (error) => {
          reject(error);
        }
      );
    });
  };

  /**
   * @typedef TagSizeType
   * @property {number} x
   * @property {number} y
   */

  /**
   * @typedef ItemListType
   * @property {number} index
   * @property {string} label
   * @property {string} value
   */

  /**
   * @param {*} printer
   * @param {TagSizeType} tagSize
   * @param {number} printDensity
   * @param {string} customTag
   * @param {boolean} barcodeEnabled
   * @param {string} logoName
   * @param {string} tagToPrint
   * @param {ItemListType} itemList
   * @param {string} workOrderStatus
   *
   */
  const printTagV1 = (
    tagSize,
    printDensity,
    customTag,
    barcodeEnabled,
    logoName,
    tagToPrint,
    itemList,
    workOrderStatus
  ) => {
    let printZPLCommands = "";
    const { x, y } = tagSize;

    const tagSizeInDots = {
      x: x * printDensity,
      y: y * printDensity
    };

    return new Promise((resolve, reject) => {
      // use custom tag if provided
      if (customTag.length) {
        // TODO: need to revisit how to support custom tag
        printZPLCommands = customTag;
      } else if (barcodeEnabled) {
        // barcode tag
        const logo = ZPLLogoCommandMap[logoName] || ZPLLogoCommandMap.defaultLogo;
        generateBarcodeTagCommand(tagSizeInDots, tagToPrint, logo);
        printZPLCommands = generateBarcodeTagCommand(tagToPrint, logo, tagSizeInDots);
      } else {
        // tag with labels
        printZPLCommands = `^XA
      ^RS8
      ${generateTagBorderCommand(tagSizeInDots)}
      ${generateWOStatusCommand(workOrderStatus, tagSizeInDots)}
      ${generateTagLabelsCommand(itemList, tagSizeInDots, workOrderStatus)}
      ^RFW,H
      ^FD${tagToPrint}^FS
      ^XZ`;
      }

      zebraPrinter.send(
        printZPLCommands,
        (success) => {
          resolve(success);
        },
        (error) => {
          reject(error);
        }
      );
    });
  };

  /**
   *
   * This function accept custom template tags with pre-filled attribute keys and a customAttributeMap to fill in the template to generate
   * a complete ZPL commands
   * @param {*} printer   a printer object
   * @param {string} customTemplate - a custom zpl template with prefilled attribute keys
   * @param {map} customAttributeMap
   */
  const printCustomTag = (customTemplate, payload) => {
    const printZPLCommands = new Function(`return \`${customTemplate}\`;`).call(payload);
    return new Promise((resolve, reject) => {
      zebraPrinter.send(
        printZPLCommands,
        (success) => {
          resolve(success);
        },
        (error) => {
          reject(error);
        }
      );
    });
  };

  return {
    isPrinterScriptsReady,
    getDefaultPrinter,
    getAvailablePrinters,
    registerPrinter,
    printTag,
    printTagV1,
    printCustomTag,
    queryPrinters,
    selectedPrinterInfo,
    setSelectedPrinterInfo,
    stopGetPrinterStatus
  };
};
